export type ApiValue = string | number | boolean | null | object | undefined
export type ApiRecord = Record<string, ApiValue>

/** Narrow an untrusted API value to a plain object, without `unknown`/`as`. */
export function isApiRecord(value: ApiValue | null): value is ApiRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export interface CanvasObservedNodeHint {
  node_id: string
  last_observed_by: 'user session' | 'director' | 'agent' | 'worker-result' | 'system' | 'unknown'
  field: string | null
  observed_at: number | null
  digest: string | null
}

export interface CanvasChangeHints {
  enabled: boolean
  nodes: CanvasObservedNodeHint[]
}

function decodeObservedActor(value: ApiValue): CanvasObservedNodeHint['last_observed_by'] {
  switch (value) {
    case 'user session':
    case 'director':
    case 'agent':
    case 'worker-result':
    case 'system':
    case 'unknown':
      return value
    default:
      throw new Error('Canvas attribution actor is invalid')
  }
}
export function decodeCanvasChangeHints(raw: ApiValue): CanvasChangeHints {
  if (!isApiRecord(raw) || typeof raw.enabled !== 'boolean' || !Array.isArray(raw.nodes)) {
    throw new Error('Canvas attribution response has invalid enabled or nodes')
  }
  const nodes = raw.nodes.map((value: ApiValue): CanvasObservedNodeHint => {
    if (!isApiRecord(value) || typeof value.node_id !== 'string') {
      throw new Error('Canvas attribution response contains an invalid node hint')
    }
    const field = value.field
    const observedAt = value.observed_at
    const digest = value.digest
    if (field !== null && field !== undefined && typeof field !== 'string') {
      throw new Error('Canvas attribution field is invalid')
    }
    if (observedAt !== null && observedAt !== undefined && typeof observedAt !== 'number') {
      throw new Error('Canvas attribution observation time is invalid')
    }
    if (digest !== null && digest !== undefined && (typeof digest !== 'string' || !/^[a-f0-9]{64}$/.test(digest))) {
      throw new Error('Canvas attribution digest is invalid')
    }
    return {
      node_id: value.node_id,
      last_observed_by: decodeObservedActor(value.last_observed_by),
      field: field ?? null,
      observed_at: observedAt ?? null,
      digest: digest ?? null,
    }
  })
  return { enabled: raw.enabled, nodes }
}

export interface DirectorToolDefinition {
  name: string
  description: string
  parameters: Record<string, ApiValue>
}

export interface DirectorToolListResponse {
  tools: DirectorToolDefinition[]
}

export interface InternalModelEntitlementItem {
  user_id: string
  email: string
  reason: string | null
  expires_at: string | null
  created_at: string
  updated_at: string
}

export interface InternalModelEntitlementListResponse {
  entitlements: InternalModelEntitlementItem[]
}

function requiredString(record: ApiRecord, key: string, context: string): string {
  const value = record[key]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${context} ${key} must be a non-empty string`)
  }
  return value
}

function nullableString(record: ApiRecord, key: string, context: string): string | null {
  const value = record[key]
  if (value !== null && typeof value !== 'string') {
    throw new Error(`${context} ${key} must be a string or null`)
  }
  return value
}

export function decodeInternalModelEntitlementItem(
  raw: ApiValue,
  context = 'Internal model entitlement response',
): InternalModelEntitlementItem {
  if (!isApiRecord(raw)) {
    throw new Error(`${context} must be an object`)
  }
  return {
    user_id: requiredString(raw, 'user_id', context),
    email: requiredString(raw, 'email', context),
    reason: nullableString(raw, 'reason', context),
    expires_at: nullableString(raw, 'expires_at', context),
    created_at: requiredString(raw, 'created_at', context),
    updated_at: requiredString(raw, 'updated_at', context),
  }
}

export function decodeInternalModelEntitlementListResponse(
  raw: ApiValue,
): InternalModelEntitlementListResponse {
  if (!isApiRecord(raw)) {
    throw new Error('Internal model entitlement list response must be an object')
  }
  if (!Array.isArray(raw.entitlements)) {
    throw new Error('Internal model entitlement list response entitlements must be an array')
  }
  return {
    entitlements: raw.entitlements.map((item, index) =>
      decodeInternalModelEntitlementItem(item, `Internal model entitlement at index ${index}`),
    ),
  }
}

export function decodeDirectorToolListResponse(
  raw: ApiValue,
): DirectorToolListResponse {
  if (!isApiRecord(raw)) {
    throw new Error('Director tool list response must be an object')
  }
  if (!Array.isArray(raw.tools)) {
    throw new Error('Director tool list response tools must be an array')
  }

  const tools = raw.tools.map((tool, index): DirectorToolDefinition => {
    if (!isApiRecord(tool)) {
      throw new Error(`Director tool at index ${index} must be an object`)
    }
    if (typeof tool.name !== 'string') {
      throw new Error(`Director tool at index ${index} name must be a string`)
    }
    if (typeof tool.description !== 'string') {
      throw new Error(
        `Director tool at index ${index} description must be a string`,
      )
    }
    if (!isApiRecord(tool.parameters)) {
      throw new Error(`Director tool at index ${index} parameters must be an object`)
    }
    return {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    }
  })

  return { tools }
}

export type VideoEditorAudioActionId =
  | 'extract_audio'
  | 'split_dialogue'
  | 'isolate_audio'
  | 'change_voice'
  | 'dub'
  | 'select_take'

export type VideoEditorAudioActionState = 'available' | 'unavailable'
export type VideoEditorAudioActionRunStatus = 'pending' | 'running' | 'completed'

export type VideoEditorAudioActionDisabledReason =
  | 'source_missing'
  | 'isolate_voice_first'
  | 'no_alternate_takes'
  | 'provider_action_not_connected'

export type VideoEditorAudioSourceKind = 'original_video_audio' | 'timeline_audio'
export type VideoEditorAudioKind =
  | 'audio'
  | 'music'
  | 'sfx'
  | 'speech'
  | 'extracted_original_audio'
  | 'changed_voice'
  | 'isolated_voice'
  | 'isolated_background'
export type VideoEditorLegacyAudioRole = 'original' | 'replacement' | 'sfx' | 'music'

export interface VideoEditorAudioAction {
  id: VideoEditorAudioActionId
  state: VideoEditorAudioActionState
  disabled_reason: VideoEditorAudioActionDisabledReason | null
}

export interface VideoEditorAudioTarget {
  id: string
  label: string
  source_kind: VideoEditorAudioSourceKind
  source_node_id: string | null
  source_path: string | null
  audio_kind: VideoEditorAudioKind | null
  legacy_role: VideoEditorLegacyAudioRole | null
  start_offset: number | null
  duration: number | null
  planned_end_offset: number | null
  actions: VideoEditorAudioAction[]
}

export interface VideoEditorAudioActionsResponse {
  action_model_version: number
  node_id: string
  revision: number | null
  targets: VideoEditorAudioTarget[]
  global_actions: VideoEditorAudioAction[]
}

export interface VideoEditorSpeechSegment {
  start_s: number
  end_s: number
  timeline_start_s: number
  timeline_end_s: number
  content_role: string
  transcript: string | null
  speaker: string | null
  language: string | null
}

export interface VideoEditorDerivedAudioClip {
  id: string
  source_node_id: string
  source_path: string
  original_duration: number
  start_time: number
  end_time: number
  volume: number
  label: string | null
  audio_kind: VideoEditorAudioKind | null
  legacy_role: VideoEditorLegacyAudioRole | null
  start_offset: number
  planned_end_offset: number | null
  speed: number
  track_index: number
  muted: boolean
  split_group_id: string | null
  peak_dbfs: number | null
}

export interface VideoEditorAudioActionRunResponse {
  action_id: VideoEditorAudioActionId
  target_id: string
  status: VideoEditorAudioActionRunStatus
  job_id: string | null
  sound_node_ids: string[]
  created_nodes: ApiRecord[]
  speech_segments: VideoEditorSpeechSegment[]
  derived_audio_clips: VideoEditorDerivedAudioClip[]
  selected_voice_name: string | null
  selected_voice_ref: string | null
  message: string | null
}

function optionalNumberOrNull(record: ApiRecord, key: string, context: string): number | null {
  const value = record[key]
  if (value === null || value === undefined) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context} ${key} must be a number or null`)
  }
  return value
}

function requiredNumber(record: ApiRecord, key: string, context: string): number {
  const value = record[key]
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${context} ${key} must be a number`)
  }
  return value
}

function optionalStringOrNull(record: ApiRecord, key: string, context: string): string | null {
  const value = record[key]
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') {
    throw new Error(`${context} ${key} must be a string or null`)
  }
  return value
}

function decodeVideoEditorAudioActionId(value: ApiValue, context: string): VideoEditorAudioActionId {
  switch (value) {
    case 'extract_audio':
    case 'split_dialogue':
    case 'isolate_audio':
    case 'change_voice':
    case 'dub':
    case 'select_take':
      return value
    default:
      throw new Error(`${context} id is invalid`)
  }
}

function decodeVideoEditorAudioActionState(value: ApiValue, context: string): VideoEditorAudioActionState {
  if (value === 'available' || value === 'unavailable') return value
  throw new Error(`${context} state is invalid`)
}

function decodeVideoEditorAudioActionRunStatus(
  value: ApiValue,
  context: string,
): VideoEditorAudioActionRunStatus {
  if (value === 'pending' || value === 'running' || value === 'completed') return value
  throw new Error(`${context} status is invalid`)
}

function decodeVideoEditorAudioDisabledReason(
  value: ApiValue,
  context: string,
): VideoEditorAudioActionDisabledReason | null {
  if (value === null || value === undefined) return null
  switch (value) {
    case 'source_missing':
    case 'isolate_voice_first':
    case 'no_alternate_takes':
    case 'provider_action_not_connected':
      return value
    default:
      throw new Error(`${context} disabled_reason is invalid`)
  }
}

function decodeVideoEditorAudioAction(
  raw: ApiValue,
  context: string,
): VideoEditorAudioAction {
  if (!isApiRecord(raw)) {
    throw new Error(`${context} must be an object`)
  }
  return {
    id: decodeVideoEditorAudioActionId(raw.id, context),
    state: decodeVideoEditorAudioActionState(raw.state, context),
    disabled_reason: decodeVideoEditorAudioDisabledReason(raw.disabled_reason, context),
  }
}

function decodeVideoEditorAudioSourceKind(value: ApiValue, context: string): VideoEditorAudioSourceKind {
  if (value === 'original_video_audio' || value === 'timeline_audio') return value
  throw new Error(`${context} source_kind is invalid`)
}

function decodeVideoEditorAudioKind(value: ApiValue, context: string): VideoEditorAudioKind | null {
  if (value === null || value === undefined) return null
  switch (value) {
    case 'audio':
    case 'music':
    case 'sfx':
    case 'speech':
    case 'extracted_original_audio':
    case 'changed_voice':
    case 'isolated_voice':
    case 'isolated_background':
      return value
    default:
      throw new Error(`${context} audio_kind is invalid`)
  }
}

function decodeVideoEditorLegacyAudioRole(value: ApiValue, context: string): VideoEditorLegacyAudioRole | null {
  if (value === null || value === undefined) return null
  if (value === 'original' || value === 'replacement' || value === 'sfx' || value === 'music') return value
  throw new Error(`${context} legacy_role is invalid`)
}

function decodeVideoEditorAudioTarget(
  raw: ApiValue,
  index: number,
): VideoEditorAudioTarget {
  const context = `Video Editor audio target ${index}`
  if (!isApiRecord(raw)) {
    throw new Error(`${context} must be an object`)
  }
  const actions = raw.actions
  if (!Array.isArray(actions)) {
    throw new Error(`${context} actions must be an array`)
  }
  return {
    id: requiredString(raw, 'id', context),
    label: requiredString(raw, 'label', context),
    source_kind: decodeVideoEditorAudioSourceKind(raw.source_kind, context),
    source_node_id: optionalStringOrNull(raw, 'source_node_id', context),
    source_path: optionalStringOrNull(raw, 'source_path', context),
    audio_kind: decodeVideoEditorAudioKind(raw.audio_kind, context),
    legacy_role: decodeVideoEditorLegacyAudioRole(raw.legacy_role ?? raw.role, context),
    start_offset: optionalNumberOrNull(raw, 'start_offset', context),
    duration: optionalNumberOrNull(raw, 'duration', context),
    planned_end_offset: optionalNumberOrNull(raw, 'planned_end_offset', context),
    actions: actions.map((action, actionIndex) =>
      decodeVideoEditorAudioAction(action, `${context} action ${actionIndex}`),
    ),
  }
}

export function decodeVideoEditorAudioActionsResponse(
  raw: ApiValue,
): VideoEditorAudioActionsResponse {
  if (!isApiRecord(raw)) {
    throw new Error('Video Editor audio actions response must be an object')
  }
  if (
    typeof raw.action_model_version !== 'number' ||
    !Number.isInteger(raw.action_model_version) ||
    raw.action_model_version < 1
  ) {
    throw new Error('Video Editor audio action model version must be a positive integer')
  }
  if (!Array.isArray(raw.targets)) {
    throw new Error('Video Editor audio actions targets must be an array')
  }
  if (!Array.isArray(raw.global_actions)) {
    throw new Error('Video Editor audio actions global_actions must be an array')
  }
  const revision = raw.revision
  if (
    revision !== null &&
    revision !== undefined &&
    (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0)
  ) {
    throw new Error('Video Editor audio actions revision must be a non-negative integer or null')
  }
  return {
    action_model_version: raw.action_model_version,
    node_id: requiredString(raw, 'node_id', 'Video Editor audio actions response'),
    revision: revision ?? null,
    targets: raw.targets.map((target, index) => decodeVideoEditorAudioTarget(target, index)),
    global_actions: raw.global_actions.map((action, index) =>
      decodeVideoEditorAudioAction(action, `Video Editor audio global action ${index}`),
    ),
  }
}

function decodeVideoEditorSpeechSegment(raw: ApiValue, index: number): VideoEditorSpeechSegment {
  const context = `Video Editor speech segment ${index}`
  if (!isApiRecord(raw)) {
    throw new Error(`${context} must be an object`)
  }
  return {
    start_s: optionalNumberOrNull(raw, 'start_s', context) ?? 0,
    end_s: optionalNumberOrNull(raw, 'end_s', context) ?? 0,
    timeline_start_s: optionalNumberOrNull(raw, 'timeline_start_s', context) ?? 0,
    timeline_end_s: optionalNumberOrNull(raw, 'timeline_end_s', context) ?? 0,
    content_role: requiredString(raw, 'content_role', context),
    transcript: optionalStringOrNull(raw, 'transcript', context),
    speaker: optionalStringOrNull(raw, 'speaker', context),
    language: optionalStringOrNull(raw, 'language', context),
  }
}

function decodeVideoEditorDerivedAudioClip(raw: ApiValue, index: number): VideoEditorDerivedAudioClip {
  const context = `Video Editor derived audio clip ${index}`
  if (!isApiRecord(raw)) {
    throw new Error(`${context} must be an object`)
  }
  return {
    id: requiredString(raw, 'id', context),
    source_node_id: requiredString(raw, 'source_node_id', context),
    source_path: requiredString(raw, 'source_path', context),
    original_duration: requiredNumber(raw, 'original_duration', context),
    start_time: optionalNumberOrNull(raw, 'start_time', context) ?? 0,
    end_time: requiredNumber(raw, 'end_time', context),
    volume: optionalNumberOrNull(raw, 'volume', context) ?? 1,
    label: optionalStringOrNull(raw, 'label', context),
    audio_kind: decodeVideoEditorAudioKind(raw.audio_kind, context),
    legacy_role: decodeVideoEditorLegacyAudioRole(raw.legacy_role ?? raw.role, context),
    start_offset: requiredNumber(raw, 'start_offset', context),
    planned_end_offset: optionalNumberOrNull(raw, 'planned_end_offset', context),
    speed: optionalNumberOrNull(raw, 'speed', context) ?? 1,
    track_index: requiredNumber(raw, 'track_index', context),
    muted: Boolean(raw.muted),
    split_group_id: optionalStringOrNull(raw, 'split_group_id', context),
    peak_dbfs: optionalNumberOrNull(raw, 'peak_dbfs', context),
  }
}

export function decodeVideoEditorAudioActionRunResponse(
  raw: ApiValue,
): VideoEditorAudioActionRunResponse {
  if (!isApiRecord(raw)) {
    throw new Error('Video Editor audio action run response must be an object')
  }
  const soundNodeIds = raw.sound_node_ids
  if (!Array.isArray(soundNodeIds) || !soundNodeIds.every((value) => typeof value === 'string')) {
    throw new Error('Video Editor audio action run response sound_node_ids must be strings')
  }
  const createdNodes = raw.created_nodes
  if (!Array.isArray(createdNodes) || !createdNodes.every(isApiRecord)) {
    throw new Error('Video Editor audio action run response created_nodes must be objects')
  }
  const speechSegments = raw.speech_segments
  if (!Array.isArray(speechSegments)) {
    throw new Error('Video Editor audio action run response speech_segments must be an array')
  }
  const derivedAudioClips = raw.derived_audio_clips
  if (!Array.isArray(derivedAudioClips)) {
    throw new Error('Video Editor audio action run response derived_audio_clips must be an array')
  }
  return {
    action_id: decodeVideoEditorAudioActionId(raw.action_id, 'Video Editor audio action run response'),
    target_id: requiredString(raw, 'target_id', 'Video Editor audio action run response'),
    status: decodeVideoEditorAudioActionRunStatus(raw.status, 'Video Editor audio action run response'),
    job_id: optionalStringOrNull(raw, 'job_id', 'Video Editor audio action run response'),
    sound_node_ids: soundNodeIds,
    created_nodes: createdNodes,
    speech_segments: speechSegments.map((segment, index) => decodeVideoEditorSpeechSegment(segment, index)),
    derived_audio_clips: derivedAudioClips.map((clip, index) => decodeVideoEditorDerivedAudioClip(clip, index)),
    selected_voice_name: optionalStringOrNull(raw, 'selected_voice_name', 'Video Editor audio action run response'),
    selected_voice_ref: optionalStringOrNull(raw, 'selected_voice_ref', 'Video Editor audio action run response'),
    message: optionalStringOrNull(raw, 'message', 'Video Editor audio action run response'),
  }
}
